import { useState, useMemo, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { startOfWeek, format, addDays, addWeeks, subWeeks, parseISO, isValid, differenceInCalendarWeeks } from 'date-fns'
import { useSwipeable } from 'react-swipeable'
import { useBreadcrumb } from '../hooks/useBreadcrumb'
import { useAuth } from '../hooks/useAuth'
import { useSimpleToast } from '../hooks/useToast'
import { trainingDiaryService } from '../services/training-diary'
import { trainingPlansService } from '../services/training-plans'
import { trainingSessionsService } from '../services/training-sessions'
import { goalsService } from '../services/goals'
import { workoutsService, type ScheduleUpdate } from '../services/workouts'
import { weeklyCheckInsService } from '../services/weekly-check-ins'
import { Skeleton } from '../components/ui/skeleton'
import { WeekNavigator } from '../components/training-diary/WeekNavigator'
import { WeekDayList } from '../components/training-diary/WeekDayList'
import { GoalFormDialog } from '../components/training-diary/GoalFormDialog'
import { UnscheduledWorkoutsSection } from '../components/training-diary/UnscheduledWorkoutsSection'
import { WeeklyCheckInCard } from '../components/training-diary/WeeklyCheckInCard'
import { TrainingPlanWeekBanner } from '../components/training-diary/TrainingPlanWeekBanner'
import { matchWorkoutResults } from '../utils/match-workout-results'
import type { TrainingPlan } from '../types/training-plan'
import type { TrainingSession } from '../types/training-session'
import type { Goal } from '../types/goal'
import type { Workout, WorkoutResult } from '../types/workout'
import type { DayAttempts, AscentItem } from '../services/training-diary'
import type { WeeklyCheckIn, WeekSummary } from '../types/weekly-check-in'
import type { SessionPayload } from '../components/training-diary/DayRow'

interface DiaryState {
  sessionsByDate: Record<string, TrainingSession>
  goalsByDate: Record<string, Goal[]>
  workouts: Workout[]
  workoutResults: WorkoutResult[]
  weeklyCheckIn: WeeklyCheckIn | null
  weekSummary?: WeekSummary
  attemptsByDate: Record<string, DayAttempts>
  ascentsByDate: Record<string, AscentItem[]>
}

const EMPTY_STATE: DiaryState = {
  sessionsByDate: {},
  goalsByDate: {},
  workouts: [],
  workoutResults: [],
  weeklyCheckIn: null,
  attemptsByDate: {},
  ascentsByDate: {},
}

function getWeekStart(date: Date): Date {
  return startOfWeek(date, { weekStartsOn: 1 })
}

// Each day is drawn in its running order; created_at breaks the tie for the
// days nobody has dragged yet.
function byRunningOrder(a: Workout, b: Workout): number {
  return a.position - b.position || a.created_at.localeCompare(b.created_at)
}

export default function TrainingDiaryPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { showError } = useSimpleToast()
  const [searchParams, setSearchParams] = useSearchParams()

  const weekStart = useMemo(() => {
    const param = searchParams.get('week')
    if (param) {
      const parsed = parseISO(param)
      if (isValid(parsed)) return getWeekStart(parsed)
    }
    return getWeekStart(new Date())
  }, [searchParams])

  // The dashboard's "start training" button links here with ?add_workout=<day>
  // to land on the diary with that day's add-workout dialog already open. Read
  // once into state, then drop the param so a reload doesn't reopen it.
  const [autoAddWorkoutDate, setAutoAddWorkoutDate] = useState<string | null>(() =>
    searchParams.get('add_workout')
  )
  const handleAutoAddWorkoutHandled = useCallback(() => setAutoAddWorkoutDate(null), [])

  // The weekly reminder links here with ?check_in=1 to land on the question it
  // is asking. Read once, then dropped, so a reload doesn't reopen the editor.
  const [openCheckIn, setOpenCheckIn] = useState(() => searchParams.get('check_in') === '1')

  useEffect(() => {
    if (!searchParams.has('add_workout') && !searchParams.has('check_in')) return
    const next = new URLSearchParams(searchParams)
    next.delete('add_workout')
    next.delete('check_in')
    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams])

  const [diary, setDiary] = useState<DiaryState>(EMPTY_STATE)
  const [loading, setLoading] = useState(true)
  const [goalDialogOpen, setGoalDialogOpen] = useState(false)
  // The athlete's own active plans, used to surface the plan banner for the week.
  const [activePlans, setActivePlans] = useState<TrainingPlan[]>([])

  // Schedule mode state
  const [scheduleMode, setScheduleMode] = useState(false)
  const [scheduleSnapshot, setScheduleSnapshot] = useState<Workout[] | null>(null)
  const [scheduleSaving, setScheduleSaving] = useState(false)

  const weekStartIso = format(weekStart, 'yyyy-MM-dd')

  useBreadcrumb(
    () => ({
      onBack: () => navigate(-1),
      segments: [{ label: t('trainingDiary.pageTitle') }],
    }),
    [t, navigate]
  )

  function handleWeekChange(date: Date) {
    setSearchParams({ week: format(getWeekStart(date), 'yyyy-MM-dd') })
  }

  const from = format(weekStart, 'yyyy-MM-dd')
  const to = format(addDays(weekStart, 6), 'yyyy-MM-dd')

  const currentUserId = user ? String(user.id) : ''

  useEffect(() => {
    if (!currentUserId) return
    trainingPlansService
      .list()
      .then((all) => setActivePlans(all.filter((p) => p.status === 'active' && p.user.id === currentUserId)))
      .catch(() => {})
  }, [currentUserId])

  // The active plan (if any) whose schedule covers the visible week.
  const coveringPlan = useMemo(() => {
    for (const plan of activePlans) {
      if (!plan.start_date || !plan.length_weeks) continue
      const planStart = startOfWeek(parseISO(plan.start_date), { weekStartsOn: 1 })
      const weekNumber = differenceInCalendarWeeks(weekStart, planStart, { weekStartsOn: 1 }) + 1
      if (weekNumber >= 1 && weekNumber <= plan.length_weeks) return { plan, weekNumber }
    }
    return null
  }, [activePlans, weekStart])

  const loadWeek = useCallback(async () => {
    setLoading(true)
    try {
      const data = await trainingDiaryService.getWeek(from, to)

      const sessionsByDate: Record<string, TrainingSession> = {}
      for (const s of data.sessions) {
        sessionsByDate[s.session_date] = s
      }

      const goalsByDate: Record<string, Goal[]> = {}
      for (const g of data.goals) {
        goalsByDate[g.target_date] = [...(goalsByDate[g.target_date] ?? []), g]
      }

      setDiary({
        sessionsByDate,
        goalsByDate,
        workouts: data.workouts ?? [],
        workoutResults: data.workout_results ?? [],
        weeklyCheckIn: data.weekly_check_in ?? null,
        weekSummary: data.week_summary,
        attemptsByDate: data.attempts_by_date,
        ascentsByDate: data.ascents_by_date ?? {},
      })
    } catch {
      // ignore, keep last state
    } finally {
      setLoading(false)
    }
  }, [from, to])

  useEffect(() => {
    loadWeek()
  }, [loadWeek])

  async function handleSessionCreate(data: SessionPayload) {
    const s = await trainingSessionsService.create(data)
    setDiary((prev) => ({
      ...prev,
      sessionsByDate: { ...prev.sessionsByDate, [s.session_date]: s },
    }))
  }

  async function handleSessionUpdate(id: string, data: SessionPayload) {
    const s = await trainingSessionsService.update(id, data)
    setDiary((prev) => {
      const next = { ...prev.sessionsByDate }
      for (const k of Object.keys(next)) {
        if (next[k].id === id) delete next[k]
      }
      next[s.session_date] = s
      return { ...prev, sessionsByDate: next }
    })
  }

  async function handleSessionDelete(id: string) {
    await trainingSessionsService.destroy(id)
    setDiary((prev) => {
      const next = { ...prev.sessionsByDate }
      for (const k of Object.keys(next)) {
        if (next[k].id === id) delete next[k]
      }
      return { ...prev, sessionsByDate: next }
    })
  }

  async function handleGoalCreate(data: { body: string; target_date: string }) {
    const g = await goalsService.create(data)
    setDiary((prev) => ({
      ...prev,
      goalsByDate: {
        ...prev.goalsByDate,
        [g.target_date]: [...(prev.goalsByDate[g.target_date] ?? []), g],
      },
    }))
  }

  async function handleGoalUpdate(id: string, data: { body: string; target_date: string }) {
    const g = await goalsService.update(id, data)
    setDiary((prev) => {
      const next = { ...prev.goalsByDate }
      for (const k of Object.keys(next)) {
        next[k] = next[k].filter((x) => x.id !== id)
        if (next[k].length === 0) delete next[k]
      }
      next[g.target_date] = [...(next[g.target_date] ?? []), g]
      return { ...prev, goalsByDate: next }
    })
  }

  async function handleWorkoutCreate(date: string, exerciseId: string) {
    const w = await workoutsService.create({ scheduled_date: date, exercise_id: exerciseId })
    setDiary((prev) => ({ ...prev, workouts: [...prev.workouts, w] }))
  }

  async function handleUnscheduledCreate(exerciseIds: string[]) {
    const created: Workout[] = []
    for (const id of exerciseIds) {
      const w = await workoutsService.create({
        scheduled_date: null,
        scheduled_week_start: weekStartIso,
        exercise_id: id,
      })
      created.push(w)
    }
    setDiary((prev) => ({ ...prev, workouts: [...prev.workouts, ...created] }))
  }

  // Creating over a week that already has an entry edits it, so one call
  // covers both writing the week and coming back to it.
  async function handleCheckInSave(body: string) {
    try {
      const saved = await weeklyCheckInsService.create({ week_start: weekStartIso, body })
      setDiary((prev) => ({ ...prev, weeklyCheckIn: saved }))
    } catch {
      showError(t('trainingDiary.weeklyCheckIn.saveError', 'Could not save your weekly log'))
      throw new Error('weekly check-in save failed')
    }
  }

  // Renumbering is applied on the spot and written straight away: a list that
  // stays where it was dropped while the server holds the old order is the one
  // outcome worth avoiding, so a failure puts it back.
  async function handleWorkoutReorder(ids: string[]) {
    const previous = diary.workouts
    const positionById = new Map(ids.map((id, index) => [id, index]))

    setDiary((prev) => ({
      ...prev,
      workouts: prev.workouts.map((w) => {
        const position = positionById.get(w.id)
        return position === undefined ? w : { ...w, position }
      }),
    }))

    try {
      await workoutsService.reorder(ids)
    } catch {
      setDiary((prev) => ({ ...prev, workouts: previous }))
      showError(t('trainingDiary.workouts.reorderError', 'Could not save the new order'))
    }
  }

  function handleEnterScheduleMode() {
    setScheduleSnapshot(diary.workouts.map((w) => ({ ...w })))
    setScheduleMode(true)
  }

  function handleWorkoutDayPick(workoutId: string, date: string | null) {
    if (!scheduleMode) return
    setDiary((prev) => ({
      ...prev,
      workouts: prev.workouts.map((w) => {
        if (w.id !== workoutId) return w
        return {
          ...w,
          scheduled_date: date,
          scheduled_week_start: date ? format(getWeekStart(parseISO(date)), 'yyyy-MM-dd') : w.scheduled_week_start,
        }
      }),
    }))
  }

  const scheduleHasChanges = useMemo(() => {
    if (!scheduleMode || !scheduleSnapshot) return false
    const snapMap = new Map(scheduleSnapshot.map((w) => [w.id, w.scheduled_date]))
    return diary.workouts.some((w) => snapMap.get(w.id) !== w.scheduled_date)
  }, [scheduleMode, scheduleSnapshot, diary.workouts])

  function exitScheduleMode() {
    setScheduleMode(false)
    setScheduleSnapshot(null)
  }

  function handleCancelSchedule() {
    if (scheduleSnapshot) {
      setDiary((prev) => ({ ...prev, workouts: scheduleSnapshot.map((w) => ({ ...w })) }))
    }
    exitScheduleMode()
  }

  async function handleSaveSchedule() {
    if (!scheduleSnapshot) return
    const snapMap = new Map(scheduleSnapshot.map((w) => [w.id, w.scheduled_date]))
    const updates: ScheduleUpdate[] = diary.workouts
      .filter((w) => snapMap.get(w.id) !== w.scheduled_date)
      .map((w) => ({ id: w.id, scheduled_date: w.scheduled_date }))
    if (updates.length === 0) {
      exitScheduleMode()
      return
    }
    setScheduleSaving(true)
    try {
      const updated = await workoutsService.bulkSchedule(updates)
      const updatedById = new Map(updated.map((w) => [w.id, w]))
      setDiary((prev) => ({
        ...prev,
        workouts: prev.workouts.map((w) => updatedById.get(w.id) ?? w),
      }))
      exitScheduleMode()
    } finally {
      setScheduleSaving(false)
    }
  }

  // Guard browser-level navigation (back/forward, reload, tab close) while
  // schedule mode has pending changes. The app uses <BrowserRouter> (not a
  // data router) so useBlocker is unavailable; we lean on history sentinel +
  // beforeunload instead.
  useEffect(() => {
    if (!(scheduleMode && scheduleHasChanges)) return

    const beforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault()
    }
    window.addEventListener('beforeunload', beforeUnload)

    // Push a sentinel so the first "back" leaves us on this URL.
    window.history.pushState({ trainingDiarySchedule: true }, '', window.location.href)
    const popState = () => {
      const proceed = window.confirm(
        t('trainingDiary.scheduleMode.unsavedBody', 'Save or discard before leaving.')
      )
      if (proceed) {
        if (scheduleSnapshot) {
          setDiary((prev) => ({ ...prev, workouts: scheduleSnapshot.map((w) => ({ ...w })) }))
        }
        exitScheduleMode()
        window.history.back()
      } else {
        window.history.pushState({ trainingDiarySchedule: true }, '', window.location.href)
      }
    }
    window.addEventListener('popstate', popState)

    return () => {
      window.removeEventListener('beforeunload', beforeUnload)
      window.removeEventListener('popstate', popState)
    }
  }, [scheduleMode, scheduleHasChanges, scheduleSnapshot, t])

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => !scheduleMode && handleWeekChange(addWeeks(weekStart, 1)),
    onSwipedRight: () => !scheduleMode && handleWeekChange(subWeeks(weekStart, 1)),
    delta: 50,
    trackMouse: false,
  })

  const workoutMatches = useMemo(
    () => matchWorkoutResults(diary.workouts, diary.workoutResults),
    [diary.workouts, diary.workoutResults]
  )

  const scheduledWorkoutsByDate = useMemo(() => {
    const byDate: Record<string, Workout[]> = {}
    for (const w of diary.workouts) {
      if (!w.scheduled_date) continue
      byDate[w.scheduled_date] = [...(byDate[w.scheduled_date] ?? []), w]
    }
    for (const date of Object.keys(byDate)) {
      byDate[date] = [...byDate[date]].sort(byRunningOrder)
    }
    return byDate
  }, [diary.workouts])

  const unscheduledWorkouts = useMemo(
    () => diary.workouts.filter((w) => w.scheduled_date == null).sort(byRunningOrder),
    [diary.workouts]
  )

  // A week that has not started yet has nothing to say about itself.
  const weekHasStarted = weekStartIso <= format(getWeekStart(new Date()), 'yyyy-MM-dd')

  // Scroll to the today row when the visible week contains today — or to the
  // weekly log when that is what the reader was sent here to write.
  useEffect(() => {
    if (loading) return
    if (openCheckIn) {
      document.getElementById('weekly-check-in')?.scrollIntoView({ block: 'center', behavior: 'auto' })
      // Once only: the card is open by now, and moving weeks from here should
      // behave like any other week.
      setOpenCheckIn(false)
      return
    }
    const today = format(new Date(), 'yyyy-MM-dd')
    if (today < from || today > to) return
    const el = document.getElementById(`day-row-${today}`)
    if (el) {
      el.scrollIntoView({ block: 'start', behavior: 'auto' })
    }
  }, [loading, from, to, openCheckIn])

  return (
    <div {...swipeHandlers} className="flex flex-col gap-4 max-w-2xl mx-auto">
      <WeekNavigator
        weekStart={weekStart}
        onWeekChange={handleWeekChange}
        onAddGoal={() => setGoalDialogOpen(true)}
        getCalendarSummary={trainingDiaryService.getCalendarSummary.bind(trainingDiaryService)}
        scheduleMode={scheduleMode}
        scheduleHasChanges={scheduleHasChanges}
        scheduleSaving={scheduleSaving}
        onEnterScheduleMode={handleEnterScheduleMode}
        onSaveSchedule={handleSaveSchedule}
        onCancelSchedule={handleCancelSchedule}
      />

      {coveringPlan && (
        <TrainingPlanWeekBanner
          plan={coveringPlan.plan}
          weekNumber={coveringPlan.weekNumber}
          weekResults={diary.workoutResults}
        />
      )}

      {loading ? (
        <div className="flex flex-col gap-3">
          {Array.from({ length: 7 }, (_, i) => (
            <Skeleton key={i} className="h-24 w-full rounded-xl" />
          ))}
        </div>
      ) : (
        <>
          <UnscheduledWorkoutsSection
            workouts={unscheduledWorkouts}
            weekStart={weekStartIso}
            scheduleMode={scheduleMode}
            onDayPick={handleWorkoutDayPick}
            onReorder={handleWorkoutReorder}
            onCreate={handleUnscheduledCreate}
          />

          <WeekDayList
            weekStart={weekStart}
            sessionsByDate={diary.sessionsByDate}
            goalsByDate={diary.goalsByDate}
            workoutsByDate={scheduledWorkoutsByDate}
            workoutMatches={workoutMatches}
            attemptsByDate={diary.attemptsByDate}
            ascentsByDate={diary.ascentsByDate}
            scheduleMode={scheduleMode}
            onSessionCreate={handleSessionCreate}
            onSessionUpdate={handleSessionUpdate}
            onSessionDelete={handleSessionDelete}
            onGoalCreate={handleGoalCreate}
            onGoalUpdate={handleGoalUpdate}
            onWorkoutCreate={handleWorkoutCreate}
            onWorkoutDayPick={handleWorkoutDayPick}
            onWorkoutReorder={handleWorkoutReorder}
            autoAddWorkoutDate={autoAddWorkoutDate}
            onAutoAddWorkoutHandled={handleAutoAddWorkoutHandled}
          />

          {weekHasStarted && !scheduleMode && (
            <WeeklyCheckInCard
              checkIn={diary.weeklyCheckIn}
              summary={diary.weekSummary}
              autoOpen={openCheckIn}
              onSave={handleCheckInSave}
            />
          )}
        </>
      )}

      <GoalFormDialog
        open={goalDialogOpen}
        onOpenChange={setGoalDialogOpen}
        onSave={handleGoalCreate}
      />

    </div>
  )
}
